import type { InsightUngroundedReason } from "./api";

/**
 * INS-1 — why an insight is not grounded, as sentences a reviewer can act on. One place, so
 * the review queue, the phone and simple mode name the same expression the same way; each
 * surface keeps its own closing sentence about what approving anyway would do.
 *
 * Empty when there is nothing specific to say — no reasons recorded (an insight flagged
 * before they were), or only `unspecified` — and the surface falls back to its "something
 * could not be named" wording.
 */
export function describeUngrounded(reasons: InsightUngroundedReason[] | null | undefined): string {
  if (!reasons) return "";

  const sentences: string[] = [];

  // A voice the diarizer separated and nobody named. Naming it on the recording does not
  // rewrite an insight already extracted, so the card says both ways out.
  const speakers = reasons
    .filter((reason) => reason.kind === "unnamed_speaker" && reason.expression)
    .map((reason) => quote(reason.expression!));
  if (speakers.length > 0) {
    sentences.push(
      `${list(speakers)} ${speakers.length === 1 ? "is a voice" : "are voices"} nobody has named yet. ` +
        `Write in who ${speakers.length === 1 ? "it was" : "they were"}, or name the speakers on the recording ` +
        "and extract its insights again.",
    );
  }

  const unresolved = reasons
    .filter((reason) => reason.kind === "unresolved" && reason.expression)
    .map((reason) => quote(reason.expression!));
  if (unresolved.length === 1) {
    sentences.push(`${unresolved[0]} could not be named from the source.`);
  } else if (unresolved.length > 1) {
    sentences.push(`These could not be named from the source: ${list(unresolved)}.`);
  }

  for (const reason of reasons) {
    if (reason.kind === "no_subject") {
      sentences.push("It does not say what it is about.");
    } else if (reason.kind === "generic_subject" && reason.expression) {
      sentences.push(`It is about ${quote(reason.expression)}, which does not say which one.`);
    } else if (reason.kind === "not_written_in" && reason.expression) {
      sentences.push(
        reason.referent
          ? `${quote(reason.expression)} was read as ${quote(reason.referent)}, but the sentence does not say so.`
          : `${quote(reason.expression)} was resolved, but the sentence does not say to what.`,
      );
    }
  }

  return sentences.join(" ");
}

function quote(text: string): string {
  return `“${text}”`;
}

function list(items: string[]): string {
  return items.length <= 2
    ? items.join(" and ")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
